import React, { useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Drawer, Typography } from '@mui/material';
import RoleTopBar from './RoleTopBar';
import ClientProjectGate from '../client/ClientProjectGate';
import StaffProjectGate from '../client/StaffProjectGate';
import { useActiveProject } from '../../context/ActiveProjectContext';
import { useRoleNav } from '../../context/RoleNavContext';
import { ProjectShellProvider } from '../../context/ProjectShellContext';
import {
  buildOrgNavItems,
  buildProjectNavItems,
  isStaffRole,
  parseProjectIdFromPath,
} from '../../utils/deepLink';
import { roleColors } from '../../theme/roleMockup';

const NAV_WIDTH = 220;

const ACCOUNT_NAV = [
  { label: 'Team', icon: '👥', path: '/app/team', clientOnly: true },
  { label: 'Billing', icon: '💳', path: '/app/billing' },
  { label: 'Settings', icon: '⚙️', path: '/app/settings' },
];

function NavItem({ item, active, badge, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        py: '9px',
        px: '14px',
        mx: 1,
        borderRadius: '6px',
        fontSize: 12.5,
        fontWeight: active ? 600 : 500,
        color: active ? roleColors.activeNavText : '#64748b',
        bgcolor: active ? roleColors.activeNavBg : 'transparent',
        cursor: 'pointer',
        transition: 'all 0.12s',
        '&:hover': {
          bgcolor: active ? roleColors.activeNavBg : '#f1f5f9',
          color: active ? roleColors.activeNavText : '#1e293b',
        },
      }}
    >
      <Box component="span" sx={{ fontSize: 15, width: 18, textAlign: 'center' }}>
        {item.icon}
      </Box>
      {item.label}
      {badge != null && badge > 0 && (
        <Box
          component="span"
          sx={{
            ml: 'auto',
            fontSize: 10,
            fontWeight: 700,
            px: '6px',
            py: '1px',
            borderRadius: '10px',
            bgcolor: item.badgeColor === 'blue' ? '#3b82f6' : '#ef4444',
            color: '#fff',
          }}
        >
          {badge}
        </Box>
      )}
    </Box>
  );
}

function NavPanel({ navGroups, user, badges, isActive, onNavigate }) {
  return (
    <Box
      sx={{
        width: NAV_WIDTH,
        height: '100%',
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
    >
      {navGroups.map((group) => (
        <Box key={group.section}>
          <Typography
            sx={{
              px: 1.5,
              pt: group.section === navGroups[0].section ? 1.5 : 1,
              pb: 0.5,
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#94a3b8',
            }}
          >
            {group.section}
          </Typography>
          {group.items
            .filter((item) => !item.clientOnly || user?.role === 'client')
            .map((item) => (
              <NavItem
                key={`${group.section}-${item.label}`}
                item={item}
                active={isActive(item)}
                badge={item.badgeKey ? badges[item.badgeKey] : null}
                onClick={() => {
                  if (!item.path) return;
                  onNavigate(item.path);
                }}
              />
            ))}
        </Box>
      ))}
    </Box>
  );
}

export default function ProjectShellLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { badges } = useRoleNav();
  const { activeProject } = useActiveProject();
  const routeProjectId = parseProjectIdFromPath(location.pathname);
  const inProject = Boolean(routeProjectId);
  const isClient = user?.role === 'client' || user?.role === 'client_team';
  const isStaff = isStaffRole(user?.role);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const navGroups = useMemo(() => {
    if (!inProject) {
      const orgItems = isStaff
        ? buildOrgNavItems(user?.role)
        : [
            { label: 'Projects', icon: '📁', path: '/app/projects' },
            { label: 'Tester Coverage', icon: '🌐', path: '/app/tester-coverage' },
          ];
      return [
        { section: isStaff ? 'Platform' : 'Get started', items: orgItems },
        { section: 'Account', items: ACCOUNT_NAV },
      ];
    }
    const title = activeProject?.title || 'Project';
    return [
      {
        section: title.length > 28 ? `${title.slice(0, 28)}…` : title,
        items: buildProjectNavItems(routeProjectId, user?.role),
      },
      ...(isStaff
        ? [{ section: 'Platform', items: buildOrgNavItems(user?.role) }]
        : []),
      { section: 'Account', items: ACCOUNT_NAV },
    ];
  }, [inProject, routeProjectId, activeProject?.title, user?.role, isStaff]);

  const isActive = (item) => {
    const path = item.path;
    if (!path) return false;
    if (path === '/app/projects') {
      return location.pathname === '/app/projects';
    }
    if (path === '/app/dashboard') {
      return location.pathname === '/app/dashboard' || location.pathname === '/dashboard';
    }
    if (path.endsWith('/cycles') || path.includes('/cycles')) {
      return (
        location.pathname === path ||
        location.pathname.startsWith(`${path}/`) ||
        (routeProjectId &&
          location.pathname.startsWith(`/app/projects/${routeProjectId}/cycles`))
      );
    }
    if (path.includes('/defects')) {
      return (
        location.pathname === path ||
        location.pathname.startsWith('/app/bugs/')
      );
    }
    if (path === '/app/team') return location.pathname === '/app/team';
    if (path === '/app/billing') return location.pathname === '/app/billing';
    if (path === '/app/settings') return location.pathname === '/app/settings';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleNavigate = (path) => {
    setMobileNavOpen(false);
    navigate(path);
  };

  const Gate = isClient ? ClientProjectGate : StaffProjectGate;

  const navProps = {
    navGroups,
    user,
    badges,
    isActive,
    onNavigate: handleNavigate,
  };

  return (
    <ProjectShellProvider nestedNav>
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: roleColors.pageBg }}>
        <RoleTopBar onMenuClick={() => setMobileNavOpen(true)} />
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Box
            component="nav"
            sx={{
              width: NAV_WIDTH,
              flexShrink: 0,
              borderRight: `1px solid ${roleColors.cardBorder}`,
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <NavPanel {...navProps} />
          </Box>

          <Drawer
            variant="temporary"
            open={mobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: NAV_WIDTH,
                borderRight: `1px solid ${roleColors.cardBorder}`,
              },
            }}
          >
            <NavPanel {...navProps} />
          </Drawer>

          <Box
            component="main"
            sx={{
              flex: 1,
              minWidth: 0,
              overflowY: 'auto',
              p: { xs: 1, sm: 2, md: 3 },
              bgcolor: roleColors.pageBg,
            }}
          >
            <Gate>{children || <Outlet />}</Gate>
          </Box>
        </Box>
      </Box>
    </ProjectShellProvider>
  );
}
